// frontend/src/components/Layout/CustomerAccountLayout.tsx
// FIX: Update role check from 'customer' to 'user'

import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import CustomerAccountSidebar from './CustomerAccountSidebar';
import CustomerAccountMobileNav from './CustomerAccountMobileNav';
import CustomerAccountBottomNav from './CustomerAccountBottomNav';
import EmailVerificationNotification from '@/components/common/EmailVerificationNotification';

interface CustomerAccountLayoutProps {
  children: React.ReactNode;
  className?: string;
}

const CustomerAccountLayout: React.FC<CustomerAccountLayoutProps> = ({ 
  children, 
  className = '' 
}) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  // 🔧 FIX: Change role check from 'customer' to 'user'
  if (!isAuthenticated || !user || user.role !== 'user') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">This page is only accessible to authenticated users.</p>
          <div className="mt-4 text-sm text-gray-500">
            <p>Debug info:</p>
            <p>Authenticated: {isAuthenticated ? 'Yes' : 'No'}</p>
            <p>User: {user ? 'Yes' : 'No'}</p>
            <p>User Role: {user?.role || 'None'}</p>
          </div>
        </div>
      </div>
    );
  }

  // Get current page info for meta/breadcrumbs
  const getCurrentPageTitle = () => {
    const path = location.pathname;
    if (path === '/account') return 'Account Overview';
    if (path.includes('/bookings')) return 'My Bookings';
    if (path.includes('/messages')) return 'Messages';
    if (path.includes('/reviews')) return 'Reviews & Ratings';
    if (path.includes('/profile')) return 'Profile';
    if (path.includes('/payments')) return 'Payment & Billing';
    if (path.includes('/settings')) return 'Settings';
    if (path.includes('/favorites')) return 'My Favorites';
    return 'My Account';
  };

  // Check if we should show the breadcrumb
  const showBreadcrumb = location.pathname !== '/account';

  return (
    <div className={`min-h-screen bg-gray-50 flex flex-col ${className}`}>
      
      {/* Success indicator */}
      <div className="bg-green-100 border-b border-green-200 px-4 py-2">
        <div className="max-w-7xl mx-auto">
          <p className="text-green-800 text-sm font-medium">
            🎉 New Customer Account Layout Active! Role: {user.role}
          </p>
        </div>
      </div>
      
      {/* Public Header - Consistent with public pages */}
      <PublicHeader showAuthButtons={true} />
      
      {/* Email Verification Notification */}
      <EmailVerificationNotification />
      
      {/* Main Content Area */}
      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          
          {/* Page Title & Breadcrumb - Mobile */}
          <div className="lg:hidden mb-4">
            <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
              <a href="/" className="hover:text-gray-700">Home</a>
              <span>/</span>
              <span className="text-gray-900">{getCurrentPageTitle()}</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {getCurrentPageTitle()}
            </h1>
          </div>

          {/* Layout Grid */}
          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            
            {/* Desktop Sidebar - Hidden on mobile */}
            <div className="hidden lg:block lg:col-span-3">
              <CustomerAccountSidebar />
            </div>
            
            {/* Mobile Navigation - Hidden on desktop */}
            <div className="lg:hidden">
              <CustomerAccountMobileNav />
            </div>
            
            {/* Main Content Area */}
            <div className="lg:col-span-9">
              
              {/* Desktop Page Header */}
              <div className="hidden lg:block mb-6">
                {showBreadcrumb && (
                  <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                    <a 
                      href="/" 
                      className="hover:text-gray-700 transition-colors"
                    >
                      Home
                    </a>
                    <span>/</span>
                    <span>Account</span>
                    <span>/</span>
                    <span className="text-gray-900 font-medium">
                      {getCurrentPageTitle()}
                    </span>
                  </nav>
                )}
                <div className="flex items-center justify-between">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {getCurrentPageTitle()}
                  </h1>
                  
                  {/* Optional: Page-specific action button */}
                  {location.pathname === '/account' && (
                    <div className="hidden sm:block">
                      <a
                        href="/services"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Book a Service
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Content Container */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-[600px]">
                
                {/* Content Padding Container */}
                <div className="p-4 sm:p-6">
                  {children}
                </div>
              </div>

              {/* Optional: Quick Actions Footer for specific pages */}
              {(location.pathname === '/account/bookings' || location.pathname === '/account') && (
                <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="mb-3 sm:mb-0">
                      <h3 className="text-sm font-medium text-blue-900">
                        Need a service?
                      </h3>
                      <p className="text-sm text-blue-700">
                        Find trusted providers in your area for any task.
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      <a
                        href="/services"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Browse Services
                      </a>
                      <a
                        href="/how-it-works"
                        className="inline-flex items-center px-4 py-2 border border-blue-300 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        How It Works
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Public Footer - Consistent with public pages */}
      <PublicFooter />
      
      {/* Bottom Navigation for Mobile */}
      <CustomerAccountBottomNav />
    </div>
  );
};

export default CustomerAccountLayout;